import { useEffect, useRef } from 'react';
import { shouldRequestOrgLoad, type OrgDepthLimit } from './org-depth-limit';

/**
 * Asks for the selected org's data once per org, and says whether the page
 * should still be held back because that request has not gone out yet
 * (PSTUDIO-4100).
 *
 * A hook of its own, not an effect inside the page, because the page imports
 * design-system Lit elements that cannot be rendered in jsdom and the part worth
 * testing is exactly the wiring: that the ref recording "already asked"
 * survives re-renders and the React 18 StrictMode replay. `requestLoad` must be
 * a stable function, or the effect re-runs on every render. LIMITATION: the
 * order of the two lines that record the ask and send it is not pinned by a
 * test, because the mock `requestLoad` does not re-render the component.
 *
 * Returns true until the request has been sent. Without it the page renders its
 * form for one frame — the machine only reports `loading` after this effect has
 * run — before swapping it for a spinner and then the real answer. It goes back
 * to false once the request is out, so a load that leaves the depth `unknown`
 * does not keep the page on its spinner.
 */
export const useOrgLoadRequest = ({
  selectedOrgId,
  depthLimit,
  canRequest,
  requestLoad,
}: {
  selectedOrgId: string | null | undefined;
  depthLimit: OrgDepthLimit;
  canRequest: boolean;
  requestLoad: () => void;
}): boolean => {
  const requestedFor = useRef<string | undefined>(undefined);

  const due = shouldRequestOrgLoad({
    selectedOrgId,
    depthLimit,
    canRequest,
    requestedFor: requestedFor.current,
  });

  useEffect(() => {
    if (
      !selectedOrgId ||
      !shouldRequestOrgLoad({
        selectedOrgId,
        depthLimit,
        canRequest,
        requestedFor: requestedFor.current,
      })
    ) {
      return;
    }

    requestedFor.current = selectedOrgId;
    requestLoad();
  }, [selectedOrgId, depthLimit, canRequest, requestLoad]);

  return due;
};
